import React from 'react'
import { useHandleCopyValue } from '@sketch/modules-common'
import {
  AttributeList,
  FullCopyAttribute,
  CollapsibleInspectorSection,
} from '../../components'
import { roundWithLocale } from '@sketch/utils'
import copy from './copy'
import { PRLayerSizingBehaviourEnum } from '@sketch-hq/sketch-web-renderer'
import { MeasureSizingBehaviour } from '../MeasureSizingBehaviour'
import { BoxModel } from '../../../BoxModel'
import {
  ElementType,
  isLayerElement,
  SketchFrameElement,
  SketchLayerElement,
} from '../../../../../../inspector'
import { ReactComponent as RectanglesIcon } from '@sketch/icons/rectangles-2-17'

interface PositionAndSizeProps {
  rotate?: number
  x: number
  y: number
  width: number
  widthBehaviour?: PRLayerSizingBehaviourEnum
  height: number
  heightBehaviour?: PRLayerSizingBehaviourEnum
  element?: SketchLayerElement | SketchFrameElement
}

export const PositionAndSize = ({
  rotate = 0,
  x,
  y,
  width,
  height,
  widthBehaviour,
  heightBehaviour,
  element,
}: PositionAndSizeProps) => {
  const roundedX = roundWithLocale(x, 2)
  const roundedY = roundWithLocale(y, 2)
  // The raw rotate value in the pr file works counter-clockwise, but users think
  // in clockwise rotation, so flip the sign
  const reversedRotate = rotate !== 0 ? -rotate : rotate
  const roundedRotate = `${roundWithLocale(reversedRotate, 2)}°`
  const roundedWidth = roundWithLocale(width, 2)
  const roundedHeight = roundWithLocale(height, 2)

  const sectionCopyValue = copy({
    rotate: roundedRotate,
    x: roundedX,
    y: roundedY,
    width: roundedWidth,
    height: roundedHeight,
  })

  const { handleCopyValue, copyTooltipText } =
    useHandleCopyValue(sectionCopyValue)

  return (
    <CollapsibleInspectorSection
      title="Position and size"
      actions={[
        {
          icon: <RectanglesIcon width={17} height={17} />,
          tooltipText: copyTooltipText,
          onClick: handleCopyValue,
        },
      ]}
      data-testid="inspector-sidebar-position"
    >
      <AttributeList>
        <FullCopyAttribute
          label="Position"
          value={`${roundedX} X · ${roundedY} Y`}
        />
        <FullCopyAttribute label="Rotate" value={roundedRotate} />
        <FullCopyAttribute
          label="Width"
          copyValue={roundedWidth}
          value={
            <>
              {roundedWidth}{' '}
              <MeasureSizingBehaviour behaviour={widthBehaviour} />
            </>
          }
        />
        <FullCopyAttribute
          label="Height"
          copyValue={roundedHeight}
          value={
            <>
              {roundedHeight}{' '}
              <MeasureSizingBehaviour behaviour={heightBehaviour} rotate />
            </>
          }
        />
      </AttributeList>

      {element &&
        isLayerElement(element) &&
        element.type !== ElementType.Group && <BoxModel element={element} />}
    </CollapsibleInspectorSection>
  )
}
